/**
 * Native iOS Firebase Database implementation using REST API
 * This bypasses WebSocket issues in iOS WKWebView
 */

import { Capacitor } from '@capacitor/core';
import { CapacitorHttp } from '@capacitor/core';
import { auth } from './firebase';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';

const DATABASE_URL = import.meta.env.VITE_FIREBASE_databaseURL;

// Prevent native auth plugin call storms when signed out or when plugin is failing.
let nativeTokenSkipUntil = 0;
let nativeTokenRequestInFlight: Promise<string | null> | null = null;
let lastNativeAuthWarnAt = 0;

function warnNativeAuthThrottled(message: string, payload?: unknown): void {
  const now = Date.now();
  // Emit at most one warning every 5 seconds.
  if (now - lastNativeAuthWarnAt < 5000) return;
  lastNativeAuthWarnAt = now;
  if (payload !== undefined) {
    console.warn(message, payload);
  } else {
    console.warn(message);
  }
}

function toErrorDiagnostics(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    const typedError = error as Error & { code?: string; errorMessage?: string };
    return {
      type: 'Error',
      name: typedError.name,
      message: typedError.message,
      code: typedError.code,
      errorMessage: typedError.errorMessage,
      stack: typedError.stack,
    };
  }

  if (typeof error === 'object' && error !== null) {
    return {
      type: 'Object',
      ...(error as Record<string, unknown>),
    };
  }

  return {
    type: typeof error,
    value: String(error),
  };
}

interface AuthTokenOptions {
  operation: 'read' | 'write';
  requireAuth?: boolean;
}

/**
 * Get auth token - tries native plugin first, then falls back to web SDK
 */
async function getAuthToken(options: AuthTokenOptions): Promise<string | null> {
  const requireAuth = !!options.requireAuth;

  // On web, skip token lookup for anonymous/public reads.
  // On native, still attempt native token lookup because auth.currentUser
  // can be temporarily null while native auth is already signed in.
  if (options.operation === 'read' && !auth.currentUser && !Capacitor.isNativePlatform()) {
    return null;
  }

  // On native platforms, get token from native plugin
  if (Capacitor.isNativePlatform()) {
    if (Date.now() >= nativeTokenSkipUntil) {
      if (!nativeTokenRequestInFlight) {
        nativeTokenRequestInFlight = (async () => {
          console.log('🔵 Native auth token request started', {
            hasWebCurrentUser: !!auth.currentUser,
            webUid: auth.currentUser?.uid ?? null,
          });

          let hasNativeUser = false;
          try {
            const currentNativeUser = await FirebaseAuthentication.getCurrentUser();
            hasNativeUser = !!currentNativeUser?.user?.uid;
            console.log('🔵 Native auth plugin current user check:', {
              hasNativeUser,
              nativeUid: currentNativeUser?.user?.uid ?? null,
              nativeEmail: currentNativeUser?.user?.email ?? null,
            });
          } catch (currentUserError) {
            warnNativeAuthThrottled('⚠️ Native getCurrentUser failed before getIdToken:', toErrorDiagnostics(currentUserError));
            nativeTokenSkipUntil = Date.now() + 15000;
            return null;
          }

          if (!hasNativeUser) {
            warnNativeAuthThrottled('⚠️ Native auth has no signed-in user; skipping getIdToken for 30s');
            nativeTokenSkipUntil = Date.now() + 30000;
            return null;
          }

          try {
            const result = await FirebaseAuthentication.getIdToken();
            if (result?.token) {
              console.log('✅ Native auth plugin returned token', {
                tokenLength: result.token.length,
                source: 'FirebaseAuthentication.getIdToken',
              });
              return result.token;
            }

            warnNativeAuthThrottled('⚠️ Native getIdToken returned no token payload:', {
              hasResult: !!result,
              resultKeys: result ? Object.keys(result) : [],
            });
            nativeTokenSkipUntil = Date.now() + 15000;
            return null;
          } catch (error) {
            warnNativeAuthThrottled('⚠️ Failed to get token from native plugin, trying web SDK:', toErrorDiagnostics(error));
            nativeTokenSkipUntil = Date.now() + 15000;
            return null;
          }
        })().finally(() => {
          nativeTokenRequestInFlight = null;
        });
      }

      const nativeToken = await nativeTokenRequestInFlight;
      if (nativeToken) {
        return nativeToken;
      }
    }
  }

  // Fall back to web SDK
  if (auth.currentUser) {
    try {
      const webToken = await auth.currentUser.getIdToken();
      console.log('✅ Web SDK returned auth token fallback', {
        tokenLength: webToken?.length ?? 0,
        uid: auth.currentUser.uid,
      });
      return webToken;
    } catch (error) {
      console.error('🔴 Failed to get token from web SDK:', toErrorDiagnostics(error));
    }
  }

  if (requireAuth) {
    throw new Error('Authentication required for this operation');
  }

  warnNativeAuthThrottled('⚠️ No auth token available from native plugin or web SDK');

  return null;
}

interface DatabaseSnapshot {
  exists(): boolean;
  val(): any;
}

class NativeDatabaseSnapshot implements DatabaseSnapshot {
  constructor(private data: any) { }

  exists(): boolean {
    return this.data !== null && this.data !== undefined;
  }

  val(): any {
    return this.data;
  }
}

/**
 * Get data from Firebase Realtime Database using REST API
 * This works reliably on iOS where WebSockets fail
 */
export async function nativeGet(path: string, options?: { throwOnError?: boolean }): Promise<DatabaseSnapshot> {
  try {
    const url = `${DATABASE_URL}/${path}.json`;
    // Get auth token from native plugin or web SDK
    const authToken = await getAuthToken({ operation: 'read' });

    const response = await CapacitorHttp.get({
      url: authToken ? `${url}?auth=${authToken}` : url,
      headers: {
        'Accept': 'application/json',
      },
    });

    if (response.status === 200) {
      return new NativeDatabaseSnapshot(response.data);
    } else {
      console.error('🔴 Native GET failed:', response.status, response.data);
      if (options?.throwOnError) throw new Error(`Database read denied or failed: ${response.status}`);
      return new NativeDatabaseSnapshot(null);
    }
  } catch (error) {
    console.error('🔴 Native GET error:', error);
    if (options?.throwOnError) throw error;
    return new NativeDatabaseSnapshot(null);
  }
}

/**
 * Get a filtered slice of a path using Realtime Database REST query parameters
 * (e.g. orderBy/startAt). Used to fetch only the *new* children of a growing
 * list instead of re-downloading the entire node on every poll.
 *
 * Query values must already be REST-encoded JSON (e.g. orderBy: '"$key"').
 */
export async function nativeGetQuery(
  path: string,
  queryParams: Record<string, string>
): Promise<DatabaseSnapshot> {
  try {
    const authToken = await getAuthToken({ operation: 'read' });

    const params = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => params.append(key, value));
    if (authToken) params.append('auth', authToken);

    const url = `${DATABASE_URL}/${path}.json?${params.toString()}`;

    const response = await CapacitorHttp.get({
      url,
      headers: {
        'Accept': 'application/json',
      },
    });

    if (response.status === 200) {
      return new NativeDatabaseSnapshot(response.data);
    } else {
      console.error('🔴 Native query GET failed:', response.status, response.data);
      return new NativeDatabaseSnapshot(null);
    }
  } catch (error) {
    console.error('🔴 Native query GET error:', error);
    return new NativeDatabaseSnapshot(null);
  }
}

/**
 * Compare-and-set transaction using Firebase REST ETags, retrying concurrent writes.
 */
export async function nativeTransaction<T>(
  path: string,
  transform: (current: T | null) => T
): Promise<T> {
  const token = await getAuthToken({ operation: 'write', requireAuth: true });
  const url = `${DATABASE_URL}/${path}.json?auth=${token}`;
  for (let attempt = 0; attempt < 10; attempt++) {
    const snapshot = await CapacitorHttp.get({
      url,
      headers: { Accept: 'application/json', 'X-Firebase-ETag': 'true' },
    });
    if (snapshot.status !== 200) throw new Error(`Failed to read transaction data: ${snapshot.status}`);
    const etag = Object.entries(snapshot.headers).find(([key]) => key.toLowerCase() === 'etag')?.[1];
    if (!etag) throw new Error('Firebase transaction response is missing its ETag');
    const updated = transform(snapshot.data as T | null);
    const response = await CapacitorHttp.request({
      method: 'PUT',
      url,
      headers: { 'Content-Type': 'application/json', 'if-match': etag },
      data: JSON.stringify(updated),
    });
    if (response.status === 200) return updated;
    if (response.status !== 412) throw new Error(`Failed to commit transaction: ${response.status}`);
  }
  throw new Error('Firebase transaction could not be committed after 10 conflicts');
}

/**
 * Set data to Firebase Realtime Database using REST API
 */
export async function nativeSet(path: string, data: any): Promise<void> {
  try {
    // console.log(`🔵 Native SET: ${path}`, typeof data, data);
    const url = `${DATABASE_URL}/${path}.json`;

    // Get auth token from native plugin or web SDK
    const authToken = await getAuthToken({ operation: 'write' });

    // Firebase REST API requires properly formatted JSON
    // Convert undefined to null (Firebase doesn't accept undefined)
    let bodyData = data;
    if (data === undefined || typeof data === 'function') {
      bodyData = null;
    }
    // CRITICAL: CapacitorHttp requires data to be sent as a string for proper JSON encoding
    // This ensures primitives like strings are properly quoted: "value" not value
    const jsonData = JSON.stringify(bodyData);
    // console.log(`🔵 Native SET JSON:`, jsonData);

    const response = await CapacitorHttp.request({
      method: 'PUT',
      url: authToken ? `${url}?auth=${authToken}` : url,
      headers: {
        'Content-Type': 'application/json',
      },
      data: jsonData,
    });

    if (response.status !== 200) {
      console.error('🔴 Native SET failed:', response.status, response.data);
      throw new Error(`Failed to set data: ${response.status}`);
    }
    // console.log(`✅ Native SET success: ${path}`);
  } catch (error) {
    console.error('🔴 Native SET error:', error);
    throw error;
  }
}

/**
 * Remove data from Firebase Realtime Database using REST API
 */
export async function nativeRemove(path: string): Promise<void> {
  try {
    const url = `${DATABASE_URL}/${path}.json`;

    // Get auth token from native plugin or web SDK
    const authToken = await getAuthToken({ operation: 'write' });

    const response = await CapacitorHttp.delete({
      url: authToken ? `${url}?auth=${authToken}` : url,
    });

    if (response.status !== 200) {
      console.error('🔴 Native REMOVE failed:', response.status, response.data);
      throw new Error(`Failed to remove data: ${response.status}`);
    }
  } catch (error) {
    console.error('🔴 Native REMOVE error:', error);
    throw error;
  }
}

/**
 * Update (PATCH) data in Firebase Realtime Database using REST API
 * Merges the provided fields without overwriting the whole node
 */
export async function nativeUpdate(path: string, data: any): Promise<void> {
  try {
    const url = `${DATABASE_URL}/${path}.json`;

    const authToken = await getAuthToken({ operation: 'write' });

    const response = await CapacitorHttp.patch({
      url: authToken ? `${url}?auth=${authToken}` : url,
      headers: {
        'Content-Type': 'application/json',
      },
      data: data,
    });

    if (response.status !== 200) {
      console.error('🔴 Native UPDATE failed:', response.status, response.data);
      throw new Error(`Failed to update data: ${response.status}`);
    }
  } catch (error) {
    console.error('🔴 Native UPDATE error:', error);
    throw error;
  }
}

/**
 * Push data to Firebase Realtime Database using REST API
 * Returns the generated key
 */
export async function nativePush(path: string, data: any): Promise<string> {
  try {
    // console.log(`🔵 Native PUSH: ${path}`, typeof data);
    const url = `${DATABASE_URL}/${path}.json`;

    // Get auth token from native plugin or web SDK
    const authToken = await getAuthToken({ operation: 'write' });

    // Firebase REST API requires properly formatted JSON
    let bodyData = data;

    // If data is undefined or a function, convert to null
    if (data === undefined || typeof data === 'function') {
      bodyData = null;
    }

    // CRITICAL: Stringify data for proper JSON encoding
    const jsonData = JSON.stringify(bodyData);

    const response = await CapacitorHttp.request({
      method: 'POST',
      url: authToken ? `${url}?auth=${authToken}` : url,
      headers: {
        'Content-Type': 'application/json',
      },
      data: jsonData,
    });

    if (response.status !== 200) {
      console.error('🔴 Native PUSH failed:', response.status, response.data);
      throw new Error(`Failed to push data: ${response.status}`);
    }

    // console.log(`✅ Native PUSH success: ${path}`, response.data);
    // Firebase returns { "name": "generated-key" }
    return response.data.name;
  } catch (error) {
    console.error('🔴 Native PUSH error:', error);
    throw error;
  }
}

// Store active polling intervals for cleanup
const activePolling = new Map<string, { interval: NodeJS.Timeout, callbacks: Set<(snapshot: DatabaseSnapshot) => void>, lastData: any, lastSentinel?: any }>();

/**
 * Subscribe to data changes using polling (for iOS)
 * Polls every 30 seconds by default to conserve Firebase reads
 * Returns unsubscribe function
 *
 * When `sentinelPath` is provided, each poll first reads a tiny sentinel node
 * (a server timestamp bumped by a Cloud Function on every write to the watched
 * node). The expensive full-node fetch only happens on the first poll and
 * whenever the sentinel value changes — otherwise the poll downloads just the
 * sentinel. This dramatically reduces data usage for large, always-on nodes
 * (results, fixtures, blogPosts, newsArticles) on iOS where the native REST
 * transport cannot hold a realtime connection.
 */
export function nativeOnValue(
  path: string,
  callback: (snapshot: DatabaseSnapshot) => void,
  pollIntervalMs: number = 30000,  // Changed from 3000ms to 30000ms (30 seconds)
  sentinelPath?: string
): () => void {
  // If already polling this path, add callback to existing poller
  if (activePolling.has(path)) {
    const poller = activePolling.get(path)!;
    poller.callbacks.add(callback);

    // Immediately call callback with cached data (even if null/undefined)
    // This ensures splash screens can finish loading
    callback(new NativeDatabaseSnapshot(poller.lastData));

    // Return unsubscribe function
    return () => {
      const poller = activePolling.get(path);
      if (poller) {
        poller.callbacks.delete(callback);
        // If no more callbacks, stop polling
        if (poller.callbacks.size === 0) {
          clearInterval(poller.interval);
          activePolling.delete(path);
        }
      }
    };
  }

  // Create new polling interval
  const callbacks = new Set<(snapshot: DatabaseSnapshot) => void>([callback]);
  let lastData: any = undefined;
  let lastSentinel: any = undefined;
  let initialized = false;

  // Emit new data to all callbacks (only when it actually changed)
  const emit = (newData: any) => {
    if (lastData === undefined || JSON.stringify(newData) !== JSON.stringify(lastData)) {
      lastData = newData;
      const poller = activePolling.get(path);
      if (poller) poller.lastData = lastData;
      const snapshotToSend = new NativeDatabaseSnapshot(newData);
      callbacks.forEach(cb => {
        try {
          cb(snapshotToSend);
        } catch (error) {
          console.error(`🔴 Callback error for ${path}:`, error);
        }
      });
    }
  };

  const pollData = async () => {
    try {
      // throwOnError: a failed read must surface here instead of returning a
      // null-data snapshot, which emit() would broadcast as phantom "empty" data
      // and overwrite good state (and below, wrongly commit the sentinel).
      if (sentinelPath) {
        // Cheap read of the tiny sentinel to decide if a full fetch is needed
        const sentinelSnap = await nativeGet(sentinelPath, { throwOnError: true });
        const newSentinel = sentinelSnap.val();

        // After the first successful load, skip the expensive full fetch when
        // the sentinel is unchanged (i.e. no writes to the watched node).
        if (initialized && JSON.stringify(newSentinel) === JSON.stringify(lastSentinel)) {
          return;
        }

        const snapshot = await nativeGet(path, { throwOnError: true });
        emit(snapshot.val());

        // Only commit the sentinel after a successful full fetch so a failed
        // fetch is retried on the next poll instead of being skipped.
        initialized = true;
        lastSentinel = newSentinel;
        const poller = activePolling.get(path);
        if (poller) poller.lastSentinel = lastSentinel;
        return;
      }

      const snapshot = await nativeGet(path, { throwOnError: true });
      emit(snapshot.val());
    } catch (error) {
      console.error(`🔴 Native polling error for ${path}:`, error);
      // Still fire callback with null data so splash screens can proceed
      if (lastData === undefined) {
        lastData = null;
        const poller = activePolling.get(path);
        if (poller) poller.lastData = lastData;
        const snapshotToSend = new NativeDatabaseSnapshot(null);
        callbacks.forEach(cb => cb(snapshotToSend));
      }
    }
  };

  // Initial fetch - always fires to unblock loading states
  pollData();

  // Set up polling
  const interval = setInterval(pollData, pollIntervalMs);

  activePolling.set(path, { interval, callbacks, lastData, lastSentinel });

  // Return unsubscribe function
  return () => {
    const poller = activePolling.get(path);
    if (poller) {
      poller.callbacks.delete(callback);
      // If no more callbacks, stop polling
      if (poller.callbacks.size === 0) {
        clearInterval(poller.interval);
        activePolling.delete(path);
      }
    }
  };
}

// Store active child polling for cleanup
const activeChildPolling = new Map<string, { interval: NodeJS.Timeout, callbacks: Set<(snapshot: DatabaseSnapshot) => void> }>();

/**
 * Subscribe to child_added events using polling (for iOS).
 *
 * Cost-optimised: the first poll fetches the node once to deliver existing
 * children and seed the latest key. Every subsequent poll only requests the
 * children added since that key, using the REST query
 * `orderBy="$key"&startAt="<lastKey>"`. Firebase push keys are chronological and
 * lexicographically ordered, and `$key` is a built-in index (no `.indexOn`
 * rule required), so this returns just the new entries instead of
 * re-downloading the whole (ever-growing) list on every poll.
 *
 * Returns unsubscribe function.
 */
export function nativeOnChildAdded(
  path: string,
  callback: (snapshot: DatabaseSnapshot) => void,
  pollIntervalMs: number = 30000  // Changed from 3000ms to 30000ms (30 seconds)
): () => void {
  // If already polling this path, add callback to existing poller
  if (activeChildPolling.has(path)) {
    const poller = activeChildPolling.get(path)!;
    poller.callbacks.add(callback);

    // Return unsubscribe function
    return () => {
      const poller = activeChildPolling.get(path);
      if (poller) {
        poller.callbacks.delete(callback);
        // If no more callbacks, stop polling
        if (poller.callbacks.size === 0) {
          clearInterval(poller.interval);
          activeChildPolling.delete(path);
        }
      }
    };
  }

  // Create new polling interval
  const callbacks = new Set<(snapshot: DatabaseSnapshot) => void>([callback]);
  let initialized = false;
  // Highest key seen so far. null means we have not yet observed any child, so
  // we keep doing cheap full fetches until the first one appears.
  let lastKey: string | null = null;

  const emitNewChildren = (data: unknown) => {
    if (!data || typeof data !== 'object') return;
    // Sort ascending so push keys are processed in chronological order.
    const keys = Object.keys(data as Record<string, unknown>).sort();
    for (const key of keys) {
      // On delta polls startAt is inclusive, so skip the boundary key we already emitted.
      if (lastKey !== null && key <= lastKey) continue;
      const childSnapshot = new NativeDatabaseSnapshot((data as Record<string, unknown>)[key]);
      callbacks.forEach((cb) => {
        try {
          cb(childSnapshot);
        } catch (error) {
          console.error(`🔴 Child callback error for ${path}:`, error);
        }
      });
      lastKey = key; // keys are ascending, so the final assignment is the max key
    }
  };

  const pollData = async () => {
    try {
      let data: unknown;
      if (!initialized || lastKey === null) {
        // First poll, or node still empty: fetch the whole node. Once it is
        // non-empty, emitNewChildren seeds lastKey and we switch to deltas.
        const snapshot = await nativeGet(path);
        data = snapshot.val();
        initialized = true;
      } else {
        // Delta poll: only fetch children added since the last key we saw.
        const snapshot = await nativeGetQuery(path, {
          orderBy: '"$key"',
          startAt: `"${lastKey}"`,
        });
        data = snapshot.val();
      }
      emitNewChildren(data);
    } catch (error) {
      console.error(`🔴 Native child polling error for ${path}:`, error);
    }
  };

  // Initial fetch
  pollData();

  // Set up polling
  const interval = setInterval(pollData, pollIntervalMs);

  activeChildPolling.set(path, { interval, callbacks });

  // Return unsubscribe function
  return () => {
    const poller = activeChildPolling.get(path);
    if (poller) {
      poller.callbacks.delete(callback);
      // If no more callbacks, stop polling
      if (poller.callbacks.size === 0) {
        clearInterval(poller.interval);
        activeChildPolling.delete(path);
      }
    }
  };
}

/**
 * Check if we should use the native REST database implementation.
 *
 * Enabled on native (iOS/Android): the Firebase Realtime Database JS SDK cannot
 * hold a realtime connection inside the Capacitor WKWebView — its WebSocket
 * stays stuck in CONNECTING (a WebKit limitation when the page is served from a
 * custom URL-scheme handler) and its long-poll transport is blocked by the
 * https://localhost origin/CORS. This REST transport uses native HTTP via
 * CapacitorHttp, bypassing the webview network stack entirely.
 *
 * Cost note: value subscriptions still poll the full node, but child_added
 * feeds now fetch only new entries via startAt queries (see nativeOnChildAdded),
 * which removes the repeated full-dataset downloads that drove iOS data usage.
 */
export function shouldUseNativeDatabase(): boolean {
  return Capacitor.isNativePlatform();
}
